import React, { useState } from 'react';
import { Database, Network, FileText, Server, Users, ChevronDown, ChevronUp } from 'lucide-react';

function App() {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <header style={{
        backgroundColor: '#ffffff',
        borderBottom: '2px solid #e9ecef',
        padding: '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ 
            color: '#2c3e50', 
            fontSize: '2.5rem', 
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <Database size={40} color="#3498db" />
            Data Models & DBMS Architecture Explorer
          </h1>
          <p style={{ color: '#6c757d', fontSize: '1.1rem', margin: 0 }}>
            Understanding Database Management Systems and Architectural Patterns
          </p>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
        
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ 
            color: '#2c3e50', 
            fontSize: '2rem', 
            marginBottom: '1.5rem',
            borderBottom: '3px solid #3498db',
            paddingBottom: '0.5rem'
          }}>
            Data Models
          </h2>

          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div 
              onClick={() => toggleSection('relational')}
              style={{ 
                cursor: 'pointer', 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ color: '#3498db', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Database size={24} />
                Relational Data Model
              </h3>
              {expandedSection === 'relational' ? <ChevronUp /> : <ChevronDown />}
            </div>
            
            {expandedSection === 'relational' && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ color: '#495057', lineHeight: '1.8' }}>
                  The relational model organizes data into tables (relations) with rows and columns. 
                  Each table represents an entity, and relationships between entities are established through keys.
                </p>
                
                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Key Concepts:</h4>
                <ul style={{ color: '#495057', lineHeight: '1.8' }}>
                  <li><strong>Tables:</strong> Structured data storage with predefined schema</li>
                  <li><strong>Primary Key:</strong> Unique identifier for each record</li>
                  <li><strong>Foreign Key:</strong> Reference to primary key in another table</li>
                  <li><strong>ACID Properties:</strong> Atomicity, Consistency, Isolation, Durability</li>
                  <li><strong>Normalization:</strong> Organizing data to reduce redundancy</li>
                </ul>

                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Example:</h4>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Students Table:</strong>
                    <pre style={{ margin: '0.5rem 0', whiteSpace: 'pre-wrap' }}>
| StudentID | Name       | DepartmentID |
|-----------|------------|--------------|
| 1         | John Doe   | 101          |
| 2         | Jane Smith | 102          |
                    </pre>
                  </div>
                  <div>
                    <strong>Departments Table:</strong>
                    <pre style={{ margin: '0.5rem 0', whiteSpace: 'pre-wrap' }}>
| DepartmentID | DeptName          |
|--------------|-------------------|
| 101          | Computer Science  |
| 102          | Mathematics       |
                    </pre>
                  </div>
                </div>

                <div style={{
                  backgroundColor: '#e7f3ff',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginTop: '1rem',
                  border: '1px solid #3498db'
                }}>
                  <p style={{ margin: 0, color: '#2c3e50' }}>
                    <strong>Use Case:</strong> Banking systems, E-commerce platforms, ERP systems
                  </p>
                </div>

                <div style={{
                  border: '2px dashed #dee2e6',
                  borderRadius: '4px',
                  padding: '3rem',
                  marginTop: '1rem',
                  textAlign: 'center',
                  backgroundColor: '#ffffff'
                }}>
                  <p style={{ color: '#6c757d', margin: 0 }}>
                    [Diagram Space: Relational Model ER Diagram]
                  </p>
                </div>
              </div>
            )}
          </div>

          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div 
              onClick={() => toggleSection('hierarchical')}
              style={{ 
                cursor: 'pointer', 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ color: '#e74c3c', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Network size={24} />
                Hierarchical Data Model
              </h3>
              {expandedSection === 'hierarchical' ? <ChevronUp /> : <ChevronDown />}
            </div>
            
            {expandedSection === 'hierarchical' && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ color: '#495057', lineHeight: '1.8' }}>
                  The hierarchical model organizes data in a tree-like structure with parent-child relationships. 
                  Each child record has only one parent, creating a strict hierarchy from root to leaves.
                </p>
                
                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Key Concepts:</h4>
                <ul style={{ color: '#495057', lineHeight: '1.8' }}>
                  <li><strong>Tree Structure:</strong> Data organized as inverted tree with root node</li>
                  <li><strong>Parent-Child Relationship:</strong> One-to-many relationships only</li>
                  <li><strong>Segments:</strong> Collection of fields representing a record type</li>
                  <li><strong>Root Node:</strong> Top-level parent with no parent of its own</li>
                  <li><strong>Navigation:</strong> Access through predefined paths from root</li>
                </ul>

                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Example:</h4>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  border: '1px solid #dee2e6'
                }}>
                  <pre style={{ whiteSpace: 'pre-wrap' }}>
Company (Root)
├── Department: Sales
│   ├── Employee: Alice (Manager)
│   └── Employee: Bob (Associate)
└── Department: Engineering
    ├── Employee: Charlie (Lead)
    └── Employee: Diana (Developer)
                  </pre>
                </div>

                <div style={{
                  backgroundColor: '#ffe7e7',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginTop: '1rem',
                  border: '1px solid #e74c3c'
                }}>
                  <p style={{ margin: 0, color: '#2c3e50' }}>
                    <strong>Use Case:</strong> File systems, XML documents, Organizational hierarchies, IBM IMS
                  </p>
                </div>

                <div style={{
                  border: '2px dashed #dee2e6',
                  borderRadius: '4px',
                  padding: '3rem',
                  marginTop: '1rem',
                  textAlign: 'center',
                  backgroundColor: '#ffffff'
                }}>
                  <p style={{ color: '#6c757d', margin: 0 }}>
                    [Diagram Space: Hierarchical Tree Structure]
                  </p>
                </div>
              </div>
            )}
          </div>

          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div 
              onClick={() => toggleSection('network')}
              style={{ 
                cursor: 'pointer', 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ color: '#9b59b6', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Network size={24} style={{ transform: 'rotate(45deg)' }} />
                Network Data Model
              </h3>
              {expandedSection === 'network' ? <ChevronUp /> : <ChevronDown />}
            </div>
            
            {expandedSection === 'network' && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ color: '#495057', lineHeight: '1.8' }}>
                  The network model extends the hierarchical model by allowing many-to-many relationships. 
                  Records can have multiple parent and child records, forming a graph structure.
                </p>
                
                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Key Concepts:</h4>
                <ul style={{ color: '#495057', lineHeight: '1.8' }}>
                  <li><strong>Graph Structure:</strong> Records connected in a network of relationships</li>
                  <li><strong>Many-to-Many:</strong> Records can have multiple parents and children</li>
                  <li><strong>Set:</strong> Named relationship between owner and member records</li>
                  <li><strong>CODASYL Standard:</strong> Conference on Data Systems Languages specification</li>
                  <li><strong>Pointers:</strong> Links between records for navigation</li>
                </ul>

                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Example:</h4>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  border: '1px solid #dee2e6'
                }}>
                  <pre style={{ whiteSpace: 'pre-wrap' }}>
Students and Courses (Many-to-Many)

Student: John
├── enrolled in → Course: Database Systems
└── enrolled in → Course: Data Structures

Student: Alice
└── enrolled in → Course: Database Systems

Course: Database Systems
├── has student → John
└── has student → Alice
                  </pre>
                </div>

                <div style={{
                  backgroundColor: '#f3e5ff',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginTop: '1rem',
                  border: '1px solid #9b59b6'
                }}>
                  <p style={{ margin: 0, color: '#2c3e50' }}>
                    <strong>Use Case:</strong> Telecommunications, IDMS, Complex relationships requiring flexibility
                  </p>
                </div>

                <div style={{
                  border: '2px dashed #dee2e6',
                  borderRadius: '4px',
                  padding: '3rem',
                  marginTop: '1rem',
                  textAlign: 'center',
                  backgroundColor: '#ffffff'
                }}>
                  <p style={{ color: '#6c757d', margin: 0 }}>
                    [Diagram Space: Network Model Graph]
                  </p>
                </div>
              </div>
            )}
          </div>

          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div 
              onClick={() => toggleSection('document')}
              style={{ 
                cursor: 'pointer', 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ color: '#27ae60', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FileText size={24} />
                Document Data Model
              </h3>
              {expandedSection === 'document' ? <ChevronUp /> : <ChevronDown />}
            </div>
            
            {expandedSection === 'document' && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ color: '#495057', lineHeight: '1.8' }}>
                  The document model stores data in semi-structured documents (usually JSON, BSON, or XML). 
                  Each document is self-describing and can have varying structures, offering flexibility.
                </p>
                
                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Key Concepts:</h4>
                <ul style={{ color: '#495057', lineHeight: '1.8' }}>
                  <li><strong>Schema-less:</strong> No fixed schema required, flexible structure</li>
                  <li><strong>Documents:</strong> Self-contained units with key-value pairs</li>
                  <li><strong>Collections:</strong> Groups of related documents</li>
                  <li><strong>Nested Data:</strong> Documents can contain arrays and sub-documents</li>
                  <li><strong>Horizontal Scaling:</strong> Easy distribution across servers</li>
                </ul>

                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Example:</h4>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  border: '1px solid #dee2e6',
                  overflow: 'auto'
                }}>
                  <pre style={{ whiteSpace: 'pre-wrap' }}>{`{
  "_id": "60d5ec49f1b2c8b1f8e4e5a1",
  "name": "John Doe",
  "email": "john@example.com",
  "age": 28,
  "address": {
    "street": "123 Main St",
    "city": "New York",
    "zipcode": "10001"
  },
  "orders": [
    {
      "order_id": "ORD001",
      "date": "2024-01-15",
      "total": 299.99
    },
    {
      "order_id": "ORD002",
      "date": "2024-02-20",
      "total": 149.50
    }
  ]
}`}</pre>
                </div>

                <div style={{
                  backgroundColor: '#e7f9f0',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginTop: '1rem',
                  border: '1px solid #27ae60'
                }}>
                  <p style={{ margin: 0, color: '#2c3e50' }}>
                    <strong>Use Case:</strong> MongoDB, CouchDB, Content Management Systems, Real-time applications
                  </p>
                </div>

                <div style={{
                  border: '2px dashed #dee2e6',
                  borderRadius: '4px',
                  padding: '3rem',
                  marginTop: '1rem',
                  textAlign: 'center',
                  backgroundColor: '#ffffff'
                }}>
                  <p style={{ color: '#6c757d', margin: 0 }}>
                    [Diagram Space: Document Structure Visualization]
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ 
            color: '#2c3e50', 
            fontSize: '2rem', 
            marginBottom: '1.5rem',
            borderBottom: '3px solid #e67e22',
            paddingBottom: '0.5rem'
          }}>
            DBMS Architecture Patterns
          </h2>

          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div 
              onClick={() => toggleSection('centralized')}
              style={{ 
                cursor: 'pointer', 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ color: '#e67e22', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Server size={24} />
                Centralized Architecture
              </h3>
              {expandedSection === 'centralized' ? <ChevronUp /> : <ChevronDown />}
            </div>
            
            {expandedSection === 'centralized' && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ color: '#495057', lineHeight: '1.8' }}>
                  In centralized architecture, the entire DBMS resides on a single computer system. 
                  All users access the database through terminals connected to the central computer.
                </p>
                
                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Characteristics:</h4>
                <ul style={{ color: '#495057', lineHeight: '1.8' }}>
                  <li><strong>Single Location:</strong> Database and DBMS software on one machine</li>
                  <li><strong>Dumb Terminals:</strong> Users connect via terminals with no processing power</li>
                  <li><strong>Central Processing:</strong> All computation happens on the main server</li>
                  <li><strong>Simple Management:</strong> Easier to maintain and backup</li>
                  <li><strong>Single Point of Failure:</strong> If server fails, entire system is down</li>
                </ul>

                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Advantages:</h4>
                <div style={{
                  backgroundColor: '#d4edda',
                  padding: '1rem',
                  borderRadius: '4px',
                  border: '1px solid #c3e6cb',
                  marginTop: '0.5rem'
                }}>
                  <ul style={{ color: '#155724', margin: 0, paddingLeft: '1.5rem' }}>
                    <li>Lower cost for small organizations</li>
                    <li>Simplified data consistency and integrity</li>
                    <li>Easy to implement security policies</li>
                    <li>Centralized backup and recovery</li>
                  </ul>
                </div>

                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Disadvantages:</h4>
                <div style={{
                  backgroundColor: '#f8d7da',
                  padding: '1rem',
                  borderRadius: '4px',
                  border: '1px solid #f5c6cb',
                  marginTop: '0.5rem'
                }}>
                  <ul style={{ color: '#721c24', margin: 0, paddingLeft: '1.5rem' }}>
                    <li>Limited scalability</li>
                    <li>Performance bottleneck with many users</li>
                    <li>High dependency on central system reliability</li>
                    <li>Not suitable for geographically distributed users</li>
                  </ul>
                </div>

                <div style={{
                  border: '2px dashed #dee2e6',
                  borderRadius: '4px',
                  padding: '3rem',
                  marginTop: '1rem',
                  textAlign: 'center',
                  backgroundColor: '#ffffff'
                }}>
                  <p style={{ color: '#6c757d', margin: 0 }}>
                    [Animation Space: Centralized Architecture Flow]
                  </p>
                  <p style={{ color: '#adb5bd', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    Terminal 1, Terminal 2, Terminal 3 → Central Server and Database
                  </p>
                </div>
              </div>
            )}
          </div>

          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div 
              onClick={() => toggleSection('clientserver')}
              style={{ 
                cursor: 'pointer', 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ color: '#16a085', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Users size={24} />
                Client-Server Architecture
              </h3>
              {expandedSection === 'clientserver' ? <ChevronUp /> : <ChevronDown />}
            </div>
            
            {expandedSection === 'clientserver' && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ color: '#495057', lineHeight: '1.8' }}>
                  Client-Server architecture separates the system into client machines (user interfaces) 
                  and server machines (database and processing). Clients send requests, servers process them 
                  and return results.
                </p>
                
                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Characteristics:</h4>
                <ul style={{ color: '#495057', lineHeight: '1.8' }}>
                  <li><strong>Distributed Processing:</strong> Workload split between clients and servers</li>
                  <li><strong>Multiple Clients:</strong> Many clients can connect simultaneously</li>
                  <li><strong>Network Communication:</strong> Clients and servers communicate over network</li>
                  <li><strong>Two-Tier or Three-Tier:</strong> Can have application servers as middle tier</li>
                  <li><strong>Smart Clients:</strong> Clients have processing capabilities</li>
                </ul>

                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Architecture Types:</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem',
                  marginTop: '1rem'
                }}>
                  <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                  }}>
                    <h5 style={{ color: '#16a085', marginTop: 0 }}>Two-Tier Architecture</h5>
                    <p style={{ color: '#495057', fontSize: '0.95rem', lineHeight: '1.6' }}>
                      Client directly communicates with database server. 
                      Business logic resides in client or server.
                    </p>
                    <p style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#6c757d' }}>
                      Client ↔ Database Server
                    </p>
                  </div>
                  <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                  }}>
                    <h5 style={{ color: '#16a085', marginTop: 0 }}>Three-Tier Architecture</h5>
                    <p style={{ color: '#495057', fontSize: '0.95rem', lineHeight: '1.6' }}>
                      Application server sits between client and database. 
                      Better separation of concerns.
                    </p>
                    <p style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#6c757d' }}>
                      Client ↔ App Server ↔ DB Server
                    </p>
                  </div>
                </div>

                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Advantages:</h4>
                <div style={{
                  backgroundColor: '#d4edda',
                  padding: '1rem',
                  borderRadius: '4px',
                  border: '1px solid #c3e6cb',
                  marginTop: '0.5rem'
                }}>
                  <ul style={{ color: '#155724', margin: 0, paddingLeft: '1.5rem' }}>
                    <li>Better scalability - add more clients or servers</li>
                    <li>Improved performance through distributed processing</li>
                    <li>Reduced network traffic (only results sent to client)</li>
                    <li>Platform independence (clients can be different OS)</li>
                    <li>Better resource utilization</li>
                  </ul>
                </div>

                <h4 style={{ color: '#2c3e50', marginTop: '1rem' }}>Disadvantages:</h4>
                <div style={{
                  backgroundColor: '#f8d7da',
                  padding: '1rem',
                  borderRadius: '4px',
                  border: '1px solid #f5c6cb',
                  marginTop: '0.5rem'
                }}>
                  <ul style={{ color: '#721c24', margin: 0, paddingLeft: '1.5rem' }}>
                    <li>More complex to implement and maintain</li>
                    <li>Higher initial cost</li>
                    <li>Network dependency</li>
                    <li>Security concerns across network</li>
                  </ul>
                </div>

                <div style={{
                  border: '2px dashed #dee2e6',
                  borderRadius: '4px',
                  padding: '3rem',
                  marginTop: '1rem',
                  textAlign: 'center',
                  backgroundColor: '#ffffff'
                }}>
                  <p style={{ color: '#6c757d', margin: 0 }}>
                    [Animation Space: Client-Server Architecture Flow]
                  </p>
                  <p style={{ color: '#adb5bd', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    (Client 1, Client 2, Client 3) ↔ (Application Server) ↔ (Database Server)
                  </p>
                </div>
              </div>
            )}
          </div>

          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ color: '#2c3e50', marginTop: 0 }}>
              Centralized vs Client-Server: Quick Comparison
            </h3>
            
            <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#2c3e50' }}>
                      Aspect
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#e67e22' }}>
                      Centralized
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#16a085' }}>
                      Client-Server
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6', fontWeight: 'bold' }}>Processing</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Single central processor</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Distributed across clients and servers</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6', fontWeight: 'bold' }}>Scalability</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Limited - difficult to scale</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Highly scalable - add more clients/servers</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6', fontWeight: 'bold' }}>Cost</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Lower initial cost</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Higher initial investment</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6', fontWeight: 'bold' }}>Maintenance</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Simpler to maintain</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>More complex maintenance</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6', fontWeight: 'bold' }}>Reliability</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Single point of failure</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Better fault tolerance</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6', fontWeight: 'bold' }}>Performance</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Bottleneck with many users</td>
                    <td style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>Better with load distribution</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '1rem', fontWeight: 'bold' }}>Best For</td>
                    <td style={{ padding: '1rem' }}>Small organizations, single location</td>
                    <td style={{ padding: '1rem' }}>Large enterprises, distributed users</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{
              border: '2px dashed #dee2e6',
              borderRadius: '4px',
              padding: '3rem',
              marginTop: '1.5rem',
              textAlign: 'center',
              backgroundColor: '#ffffff'
            }}>
              <p style={{ color: '#6c757d', margin: 0 }}>
                [Animation Space: Side-by-Side Comparison Diagram]
              </p>
              <p style={{ color: '#adb5bd', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                Visual flow comparison between both architectures
              </p>
            </div>
          </div>
        </section>

        <footer style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: '2rem',
          marginTop: '3rem',
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#2c3e50', marginTop: 0 }}>Summary</h3>
          <p style={{ color: '#495057', lineHeight: '1.8', maxWidth: '800px', margin: '0 auto' }}>
            Understanding data models and DBMS architectures is crucial for designing efficient database systems. 
            Choose the right data model based on your data structure needs, and select the appropriate 
            architecture pattern based on your organization's size, scalability requirements, and budget constraints.
          </p>
        </footer>
      </main>
    </div>
  );
}

export default App;
